import { ImagePicker } from "@components/ImagePicker";
import { Input } from "@components/Input";
import { Link } from "@components/Link";
import { Loading } from "@components/Loading";
import { useTranslation } from "@helpers/useTranslation";
import { doc, updateDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { useCallback, useContext, useEffect, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { useForm } from "react-hook-form";
import { FirebaseContext } from "src/helpers/firebase";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useFirebaseCachedImageUrl } from "src/helpers/useFirebaseCachedImageUrl";
import { useHasNoPin } from "src/helpers/useHasNoPin";
import { usePersistedState } from "src/helpers/usePersistedState";
import { useToast } from "src/helpers/useToast";

const fetchImageAsFile = async (url: string, filename: string): Promise<File> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
};

export const NewTeam = () => {
  const { t } = useTranslation("newteam");
  const navigate = useEventNavigate();
  const eventId = useEventId();
  const [event, loadingEvent, errorEvent] = useEvent();
  const [teamAlreadyExists, setTeamAlreadyExists] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      newTeamNameValue: "",
      newTeamEmailValue: "",
      newTeamPhoneValue: "",
      newTeamPasswordValue: "",
      contact: true,
    },
  });

  const teamName = watch("newTeamNameValue");
  useEffect(() => {
    if (teamName) setTeamAlreadyExists(false);
  }, [teamName]);

  const isPromoEvent = event?.isPromo ?? false;

  const { pinAuth, storage, functions, firestore } = useContext(FirebaseContext);
  const [appData, setAppData] = usePersistedState();
  const [image, setImage] = useState<undefined | File>(undefined);
  const [defaultImageLoaded, setDefaultImageLoaded] = useState(false);
  const [exchangePinForToken, _, errorExchangePin] = useHttpsCallable(functions, "exchangePinForTokenV2");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const isOnninenLV = eventId === "vasaras-diena-liepaja-120626";

  const defaultAvatarUrl = useFirebaseCachedImageUrl(`events/${eventId}/defaultAvatar_heavy`);
  const useDefaultProfilePicture = (isPromoEvent || isOnninenLV) && !!defaultAvatarUrl;
  const defaultProfilePicture = useDefaultProfilePicture ? defaultAvatarUrl : undefined;

  // Load default profile picture if enabled
  useEffect(() => {
    if (useDefaultProfilePicture && defaultProfilePicture && !defaultImageLoaded) {
      fetchImageAsFile(defaultProfilePicture, "default-profile.png")
        .then((file) => {
          setImage(file);
          setDefaultImageLoaded(true);
        })
        .catch((err) => console.error("Failed to load default profile picture:", err));
    }
  }, [useDefaultProfilePicture, defaultProfilePicture, defaultImageLoaded]);

  const handleImageChange = useCallback((file: File) => {
    setImage(file);
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    const pin = appData?.pin;
    const { newTeamEmailValue, contact, newTeamPasswordValue } = data;
    try {
      const teamName = data.newTeamNameValue;

      const displayName = teamName;
      const sanitisedName = teamName.replace(/[^a-zA-Z0-9]/g, "");
      const instagram = data.instagram ? data.instagram.replace("@", "") : null;

      const tokenResponse = await exchangePinForToken({
        pin,
        eventId,
        teamName: sanitisedName,
        displayName,
        email: newTeamEmailValue,
        instagram,
        contact,
        password: newTeamPasswordValue,
        isNew: true,
      });
      const token = tokenResponse?.data as string | undefined;
      const valid = token !== undefined;
      if (!valid) return;

      const authResponse = await pinAuth(token);
      const uid = authResponse.user?.uid;
      const deeplink = appData?.deeplink;
      setAppData({ ...appData, uid, isUploader: false, deeplink: undefined });

      // Upload team avatar
      const imagePath = `events/${eventId}/teams/${uid}/avatar`;
      const imageRef = storageRef(storage, imagePath);
      await uploadBytes(imageRef, image);

      // ── ONNINEN LV (event-specific, throwaway) ──────────────────────────
      if (isOnninenLV) {
        await updateDoc(doc(firestore, `events/${eventId}/teams/${uid}`), {
          phone: data.newTeamPhoneValue ?? "",
        });
      }
      // ──────────────────────────────────────────────────────────────────

      navigate(deeplink ?? "/challenges");
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (errorExchangePin) {
      if (errorExchangePin.message.includes("Invalid PIN code")) {
        toast(t("invalidPinCode"), "🤷‍♂️", "error");
        navigate("/intro");
      } else if (errorExchangePin.message.includes("Team name already exists")) {
        setTeamAlreadyExists(true);
        toast(t("teamNameAlreadyExists"), "🤷‍♂️", "error");
      } else {
        toast(t("errorCreatingTeam"), "🤷‍♂️", "error");
      }
      console.error(errorExchangePin);
      setLoading(false);
    }
  }, [errorExchangePin]);

  useEffect(() => {
    if (errorEvent) console.error(errorEvent);
  }, [errorEvent]);

  useHasNoPin();

  const isPlayers = event?.mainConfig?.playersOnly ?? false;
  const title = isPlayers ? t("titlePlayers") : t("title");

  let about = isPlayers ? t("aboutPlayers") : t("about");
  if (isPromoEvent) about = t("aboutPromo");

  const labelName = isPlayers ? t("labelPlayerName") : t("labelTeamName");
  const placeholderName = isPlayers ? t("placeholderPlayerName") : t("placeholderTeamName");
  const password = isPlayers ? t("playerPassword") : t("teamPassword");
  const teamPhoto = isPlayers ? t("playerPhoto") : t("teamPhoto");
  const teamNameAlreadyExists = isPlayers ? t("playerNameAlreadyExists") : t("teamNameAlreadyExists");
  const passwordTooltip = isPlayers ? t("playerPasswordTooltip") : t("teamPasswordTooltip");

  const collectInstagram = event?.mainConfig?.collectInstagram ?? false;

  const collectEmail = isOnninenLV || isPromoEvent;
  const collectPhone = isOnninenLV;

  const isElgamaFiestaLatina = eventId === "elgamafiestalatina";

  return (
    <Loading loading={!event || loadingEvent}>
      <div className="flex h-screen flex-col overflow-hidden">
        <main className="flex-1 overflow-y-scroll p-4">
          <div className={"px-4"}>
            <h1 className="mt-16 mb-8 text-center  text-4xl font-semibold">{title}</h1>
            <h1 className="mt-4 mb-8 text-center text-lg">{about}</h1>
            <form onSubmit={handleSubmit(onSubmit)} className="h-100">
              <div className="flex w-full flex-col px-4">
                <div className="align-center mx-auto flex w-full max-w-sm">
                  <Input
                    label={labelName}
                    name="newTeamNameValue"
                    autoComplete="new-password"
                    required
                    placeholder={placeholderName}
                    requiredMessage={t("fieldRequired")}
                    errors={errors}
                    register={register}
                    className="max-w-md"
                  />
                </div>
                {collectEmail && (
                  <>
                    <span className="mt-2" />
                    <div className="align-center mx-auto flex w-full max-w-sm">
                      <Input
                        label={t("email")}
                        name="newTeamEmailValue"
                        autoComplete="new-password"
                        required
                        placeholder={t("emailExample")}
                        type="email"
                        requiredMessage={t("fieldRequired")}
                        errors={errors}
                        register={register}
                      />
                    </div>
                  </>
                )}
                {collectPhone && (
                  <>
                    <span className="mt-2" />
                    <div className="align-center mx-auto flex w-full max-w-sm">
                      <Input
                        label={t("phone")}
                        name="newTeamPhoneValue"
                        autoComplete="new-password"
                        required
                        placeholder={t("phoneExample")}
                        type="tel"
                        requiredMessage={t("fieldRequired")}
                        errors={errors}
                        register={register}
                      />
                    </div>
                  </>
                )}
                {collectInstagram && (
                  <>
                    <span className="mt-2" />
                    <div className="align-center mx-auto flex w-full max-w-sm">
                      <Input
                        label={t("instagram")}
                        name="instagram"
                        prefix="@"
                        placeholder={t("instagramExample")}
                        requiredMessage={t("fieldRequired")}
                        errors={errors}
                        register={register}
                        autoComplete="on"
                        tooltip={t("instagramTooltip")}
                      />
                    </div>
                  </>
                )}

                {teamAlreadyExists && (
                  <>
                    <div className="mt-2 mb-4 text-center text-xs text-neutral">
                      <div className="text-error">{teamNameAlreadyExists}</div>
                      <div className="mt-1 text-xs">
                        {`${t("joinExistingTeam")} ${teamName} `}
                        <Link className="underline" to="selectteam">
                          {t("clickHere")}.
                        </Link>
                      </div>
                    </div>
                  </>
                )}

                {!isElgamaFiestaLatina && (
                  <>
                    <span className="mt-2" />
                    <div className="align-center mx-auto flex w-full max-w-sm">
                      <Input
                        label={password}
                        name="newTeamPasswordValue"
                        autoComplete="new-password"
                        type="password"
                        metaCopy={isPromoEvent ? t("optional") : undefined}
                        placeholder={t("passwordPlaceholder")}
                        requiredMessage={t("fieldRequired")}
                        errors={errors}
                        register={register}
                        tooltip={passwordTooltip}
                      />
                    </div>
                  </>
                )}
                <ImagePicker
                  allowUpload
                  label={teamPhoto}
                  uploadLabel={useDefaultProfilePicture ? t("changePhoto") : t("uploadPhoto")}
                  onNewImage={handleImageChange}
                  defaultImageUrl={defaultProfilePicture}
                />
                <div className="flex justify-center">
                  <div
                    className={` ${!image ? "tooltip tooltip-top" : ""}`}
                    data-tip={!image ? t("uploadPhotoTooltip") : ""}
                  >
                    <div className="mx-12 mb-12 mt-6 flex flex-col">
                      <button
                        disabled={loading || !image}
                        className={`${loading ? "loading" : ""} btn-primary btn mb-4 max-w-lg`}
                      >
                        {t("create")}
                      </button>
                      <div>
                        {collectEmail && (
                          <div className="text-muted max-w-xs px-4 text-center text-2xs">
                            <span>{t("confirmThatYouAgreeSharingEmail")}</span>{" "}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
    </Loading>
  );
};
